import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  MessageSquare, Send, Plus, Trash2, Loader2, Sparkles, CheckCircle2, Bot, User as UserIcon, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import {
  createChatSession,
  listChatSessions,
  getChatSession,
  deleteChatSession,
  sendChatMessage,
  executeChatAction,
} from "@/lib/api";
import { formatIndianDate } from "@/lib/date";

export default function Chat() {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);  // {id, title, ...}
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [executing, setExecuting] = useState({});  // {messageId: bool}
  const scrollRef = useRef(null);

  // Load sessions list on mount
  useEffect(() => {
    refreshSessions();
  }, []);

  // Load thread when sessionId changes
  useEffect(() => {
    if (!sessionId) {
      setActive(null);
      setMessages([]);
      return;
    }
    setLoadingThread(true);
    getChatSession(sessionId)
      .then((r) => {
        setActive(r.data.session);
        setMessages(r.data.messages || []);
      })
      .catch(() => toast.error("Failed to load chat"))
      .finally(() => setLoadingThread(false));
  }, [sessionId]);

  // Auto-scroll on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const refreshSessions = () =>
    listChatSessions().then((r) => setSessions(r.data || [])).catch(() => {});

  const handleNewChat = async () => {
    try {
      const r = await createChatSession();
      await refreshSessions();
      navigate(`/chat/${r.data.id}`);
    } catch {
      toast.error("Could not create new chat");
    }
  };

  const handleDelete = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete this chat? This cannot be undone.")) return;
    try {
      await deleteChatSession(id);
      await refreshSessions();
      if (id === sessionId) navigate("/chat");
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text) return;
    let sid = sessionId;
    if (!sid) {
      // Create on first send
      try {
        const r = await createChatSession();
        sid = r.data.id;
        navigate(`/chat/${sid}`);
      } catch {
        toast.error("Could not start chat");
        return;
      }
    }
    setInput("");
    setSending(true);
    // Optimistic user message
    const optimistic = {
      id: `temp-${Date.now()}`,
      role: "user",
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimistic]);
    try {
      const r = await sendChatMessage(sid, text);
      const assistant = r.data.assistant;
      // Replace optimistic + add assistant
      setMessages((m) => {
        const withoutTemp = m.filter((x) => x.id !== optimistic.id);
        // Refetch real user msg via reload (cheap):
        return [
          ...withoutTemp,
          { ...optimistic, id: `u-${assistant.id}` },  // keep displayed user msg
          assistant,
        ];
      });
      refreshSessions();
    } catch (e) {
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const handleExecute = async (msg) => {
    setExecuting((s) => ({ ...s, [msg.id]: true }));
    try {
      const r = await executeChatAction(sessionId, msg.id);
      const data = r.data;
      // Reload thread (executed flag updated server-side)
      const fresh = await getChatSession(sessionId);
      setMessages(fresh.data.messages || []);
      toast.success(`Created ${data.resolution_ids?.length || 0} resolution${(data.resolution_ids?.length || 0) === 1 ? "" : "s"} across ${data.meetings?.length || 0} meeting${(data.meetings?.length || 0) === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Execution failed");
    } finally {
      setExecuting((s) => ({ ...s, [msg.id]: false }));
    }
  };

  return (
    <div className="flex h-[calc(100vh-72px)]" data-testid="chat-page">
      {/* --- Sidebar --- */}
      <aside className="w-72 border-r border-zinc-200 bg-white flex flex-col shrink-0">
        <div className="p-4 border-b border-zinc-200">
          <button
            onClick={handleNewChat}
            className="swiss-btn-primary w-full justify-center"
            data-testid="chat-new"
          >
            <Plus size={14} /> New chat
          </button>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="chat-sessions-list">
          {sessions.length === 0 && (
            <div className="p-6 text-xs text-zinc-500 text-center">
              No chats yet. Start a new conversation.
            </div>
          )}
          {sessions.map((s) => (
            <button
              key={s.id}
              onClick={() => navigate(`/chat/${s.id}`)}
              className={`group w-full text-left px-4 py-3 border-b border-zinc-100 hover:bg-zinc-50 ${
                s.id === sessionId ? "bg-[#002FA7]/5 border-l-2 border-l-[#002FA7]" : ""
              }`}
              data-testid={`chat-session-${s.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-900 truncate">{s.title || "Untitled chat"}</div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">{formatIndianDate(s.updated_at)}</div>
                </div>
                <span
                  onClick={(e) => handleDelete(s.id, e)}
                  className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-rose-600 transition"
                  title="Delete"
                  data-testid={`chat-delete-${s.id}`}
                >
                  <Trash2 size={13} />
                </span>
              </div>
            </button>
          ))}
        </div>
      </aside>

      {/* --- Main pane --- */}
      <main className="flex-1 flex flex-col min-w-0 bg-zinc-50">
        {!sessionId ? (
          <EmptyState onNew={handleNewChat} />
        ) : (
          <>
            <header className="px-6 py-4 border-b border-zinc-200 bg-white flex items-center gap-3">
              <Bot size={18} className="text-[#002FA7]" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{active?.title || "Chat"}</div>
                <div className="text-[11px] text-zinc-500">ROC Vault Copilot · Claude Sonnet 4.5</div>
              </div>
            </header>

            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-6 py-6 space-y-4"
              data-testid="chat-thread"
            >
              {loadingThread ? (
                <div className="text-center text-sm text-zinc-500 py-12 flex items-center justify-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Loading…
                </div>
              ) : messages.length === 0 ? (
                <ThreadIntro onPick={(p) => setInput(p)} />
              ) : (
                messages.map((m) => (
                  <MessageBubble
                    key={m.id}
                    msg={m}
                    onExecute={() => handleExecute(m)}
                    executing={!!executing[m.id]}
                  />
                ))
              )}
              {sending && (
                <div className="flex items-start gap-3" data-testid="chat-typing">
                  <div className="w-8 h-8 rounded-full bg-[#002FA7]/10 flex items-center justify-center shrink-0">
                    <Bot size={14} className="text-[#002FA7]" />
                  </div>
                  <div className="bg-white border border-zinc-200 px-4 py-3 text-sm text-zinc-500 flex items-center gap-2">
                    <Loader2 size={13} className="animate-spin" /> Thinking…
                  </div>
                </div>
              )}
            </div>

            <footer className="p-4 border-t border-zinc-200 bg-white">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-end gap-3 border border-zinc-300 bg-white focus-within:ring-2 focus-within:ring-[#002FA7] focus-within:border-[#002FA7]">
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    placeholder="Ask the Copilot — e.g. ‘Plan the annual compliance docket for ACME Pvt Ltd for FY 2025-26’"
                    rows={2}
                    className="flex-1 px-4 py-3 text-sm resize-none focus:outline-none bg-transparent"
                    disabled={sending}
                    data-testid="chat-input"
                  />
                  <button
                    onClick={handleSend}
                    disabled={sending || !input.trim()}
                    className="m-1.5 px-3 py-2.5 bg-[#002FA7] text-white text-sm disabled:opacity-40 hover:bg-[#002280] flex items-center gap-2"
                    data-testid="chat-send"
                  >
                    {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  </button>
                </div>
                <p className="text-[11px] text-zinc-500 mt-2">
                  Shift + Enter for newline · Plans must be confirmed before drafts are saved.
                </p>
              </div>
            </footer>
          </>
        )}
      </main>
    </div>
  );
}

function MessageBubble({ msg, onExecute, executing }) {
  const isUser = msg.role === "user";
  const content = isUser ? msg.content : (msg.prose || msg.content || "");
  const action = msg.action;
  const hasValidAction = action && action.type === "draft_meetings" && !action._invalid_reason;
  const invalidReason = action?._invalid_reason;
  const executed = msg.executed;

  return (
    <div className={`flex items-start gap-3 ${isUser ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${msg.role}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
        isUser ? "bg-zinc-200" : "bg-[#002FA7]/10"
      }`}>
        {isUser ? <UserIcon size={14} className="text-zinc-600" /> : <Bot size={14} className="text-[#002FA7]" />}
      </div>
      <div className={`max-w-[80%] ${isUser ? "items-end" : "items-start"} flex flex-col gap-2`}>
        <div className={`px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${
          isUser ? "bg-[#002FA7] text-white" : "bg-white border border-zinc-200"
        }`}>
          {content}
        </div>

        {hasValidAction && (
          <ActionCard action={action} executed={executed} executing={executing} onExecute={onExecute} executedMeetingIds={msg.executed_meeting_ids || []} testId={`chat-action-${msg.id}`} />
        )}
        {invalidReason && (
          <div className="border border-amber-300 bg-amber-50 p-3 text-[12px] text-amber-800 flex items-start gap-2 max-w-xl">
            <AlertCircle size={13} className="mt-0.5 shrink-0" />
            <div>
              <div className="font-medium mb-0.5">Plan looks off — please retry.</div>
              <div className="text-amber-700">{invalidReason}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ActionCard({ action, executed, executing, onExecute, executedMeetingIds, testId }) {
  const meetings = action.meetings || [];
  return (
    <div className="border border-[#002FA7]/40 bg-[#002FA7]/5 max-w-2xl" data-testid={testId}>
      <div className="px-4 py-2 border-b border-[#002FA7]/30 flex items-center gap-2 bg-[#002FA7]/10">
        <Sparkles size={13} className="text-[#002FA7]" />
        <span className="text-[11px] uppercase tracking-[0.15em] text-[#002FA7] font-medium">Proposed Plan</span>
        {executed && (
          <span className="ml-auto text-[10px] uppercase tracking-wider px-2 py-0.5 bg-emerald-100 text-emerald-700 flex items-center gap-1">
            <CheckCircle2 size={11} /> Drafted &amp; saved
          </span>
        )}
      </div>
      <div className="p-4 space-y-3">
        {meetings.map((m, mi) => (
          <div key={`${m.meeting_date}-${m.title || mi}`} className="bg-white border border-zinc-200 p-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-zinc-300 text-zinc-600">{m.meeting_type}</span>
              <span className="text-[13px] font-medium text-zinc-900">{m.title}</span>
              <span className="ml-auto text-[11px] text-zinc-500">{formatIndianDate(m.meeting_date) || m.meeting_date}</span>
            </div>
            <ul className="text-[12px] text-zinc-700 space-y-1">
              {(m.items || []).map((it, ii) => (
                <li key={`${it.resolution_type_key}-${ii}`} className="flex gap-2">
                  <span className="font-mono-plex text-zinc-400">{ii + 1}.</span>
                  <span>
                    {it.custom_title || it.resolution_type_key}
                    {it.is_special_resolution && (
                      <span className="ml-2 text-[9px] tracking-wider uppercase text-[#002FA7] border border-[#002FA7] px-1">SR</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}

        {executed ? (
          <div className="text-[12px] text-emerald-700 flex flex-wrap gap-x-3 gap-y-1">
            {executedMeetingIds.length > 0 && executedMeetingIds.map((mid) => (
              <Link key={mid} to={`/meetings/${mid}`} className="underline" data-testid={`chat-action-meeting-${mid}`}>
                View meeting →
              </Link>
            ))}
            {executedMeetingIds.length === 0 && (
              <Link to="/resolutions" className="underline">View resolutions →</Link>
            )}
          </div>
        ) : (
          <button
            onClick={onExecute}
            disabled={executing}
            className="swiss-btn-primary"
            data-testid={`chat-execute-${testId}`}
          >
            {executing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {executing ? "Drafting…" : `Confirm & Draft ${meetings.reduce((s, m) => s + (m.items?.length || 0), 0)} Resolution${meetings.reduce((s, m) => s + (m.items?.length || 0), 0) === 1 ? "" : "s"}`}
          </button>
        )}
      </div>
    </div>
  );
}

function EmptyState({ onNew }) {
  return (
    <div className="flex-1 flex items-center justify-center p-10">
      <div className="text-center max-w-md">
        <div className="w-14 h-14 mx-auto rounded-full bg-[#002FA7]/10 flex items-center justify-center mb-4">
          <MessageSquare size={22} className="text-[#002FA7]" />
        </div>
        <h2 className="font-heading text-2xl font-light tracking-tight mb-2">ROC Vault Copilot</h2>
        <p className="text-sm text-zinc-600 mb-6">
          Conversational AI for Indian company compliance. Ask anything — draft resolutions, plan annual filings, look up sections.
        </p>
        <button onClick={onNew} className="swiss-btn-primary" data-testid="chat-empty-new">
          <Plus size={14} /> Start a new chat
        </button>
      </div>
    </div>
  );
}

function ThreadIntro({ onPick }) {
  const prompts = useMemo(
    () => [
      "Plan the annual compliance docket for FY 2025-26.",
      "Draft a Section 186 investment resolution for our subsidiary.",
      "What's the due date for MGT-7 this year?",
      "Generate a banking resolution to open a new HDFC current account.",
    ],
    [],
  );
  return (
    <div className="max-w-2xl mx-auto py-10 text-center" data-testid="chat-thread-intro">
      <div className="w-12 h-12 mx-auto rounded-full bg-[#002FA7]/10 flex items-center justify-center mb-4">
        <Bot size={20} className="text-[#002FA7]" />
      </div>
      <h2 className="font-heading text-xl font-light tracking-tight mb-2">How can I help with compliance today?</h2>
      <p className="text-sm text-zinc-500 mb-6">Try one of these — or ask your own.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-left">
        {prompts.map((p) => (
          <button
            key={p}
            onClick={() => {
              onPick(p);
              const inp = document.querySelector('[data-testid="chat-input"]');
              if (inp) inp.focus();
            }}
            className="text-[13px] border border-zinc-200 bg-white hover:border-[#002FA7] hover:bg-[#002FA7]/5 px-3 py-2 transition"
            data-testid={`chat-prompt-${prompts.indexOf(p)}`}
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}
